import React, { useEffect, useRef } from 'react';
import './styles.css'; // Import the prefixed CSS

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Due Diligence Content) ---
import {
    // Icons for Core Concepts / Benefits / Imperatives
    faMagnifyingGlassChart, // Due Diligence / Assessment
    faShieldAlt,          // Risk / Compliance / Protection
    faHandHoldingDollar,  // Value / Investment / Financial
    faClipboardCheck,     // Compliance / Audit / Integrity
    faBinoculars,         // Insights / Forward-Looking
    faBuildingShield,     // Institutional Integrity / Reputation
    faRoad,               // Roadmap / Strategy
    faHeartPulse,         // Patient Care / Clinical Outcomes
    // Icons for the 6 Types of Due Diligence
    faCogs,               // Operational
    faCalculator,         // Financial
    faUsers,              // Human Resource
    faGavel,              // Legal & Regulatory
    faBuilding,           // Infrastructure
    faUserMd,             // Clinical Governance (Alternative: faNotesMedical)
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faBalanceScale, faArrowTrendUp, faScissors, faBullseye, faThumbsUp
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons

// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.3 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // For "Why Indispensable?" (Features)
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // For "Strategic Imperative" (Detailed Planning)
    const [title5Ref, title5Visible] = useIntersectionObserverTitle(titleObserverOptions); // For "How it's Conducted" (Methodology/New Section)
    // const [title6Ref, title6Visible] = useIntersectionObserverTitle(titleObserverOptions); // Removed Results Section
    // const [title7Ref, title7Visible] = useIntersectionObserverTitle(titleObserverOptions); // Removed Why Choose Us Section

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for due diligence
    const heroBgImage = 'https://images.unsplash.com/photo-1554774853-719586f82d77?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Image with charts/analysis

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Hospital Due Diligence Services</h1>
            <p className="cliniqor-sub-heading">
              Empowering Healthcare Decisions with Strategic Intelligence
            </p>
            <p className="cliniqor-intro-text">
              A specialized, multi-dimensional evaluation process delivering a 360-degree view of a hospital's strategic, clinical, financial, legal, and operational standing for confident, risk-mitigated transactions and transformations.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- INTRO SECTION --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <h3>The Foundation for Healthcare Transactions</h3>
                     <p>The primary objective is to empower stakeholders—investors, boards, lenders, and consultants—with reliable, data-driven intelligence supporting value-maximizing decisions for mergers, acquisitions, investments, and turnarounds.</p>
                     <p>Cliniqor offers expert hospital due diligence services combining deep domain knowledge with data-driven analytics. It's a strategic mandate to future-proof investments, protect public health, and unlock growth.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
                 {/* Optional: Add a Core Components list here or in a separate section if needed */}
                 {/* <div className="cliniqor-intro-list cliniqor-fade-in-up"> ... </div> */}
             </div>
          </div>
        </section>

        {/* --- WHY INDISPENSABLE? (Features Section) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Why is Healthcare Due Diligence Indispensable?</h2>
              <p>An institution-wide audit of integrity, risk, performance, and strategic alignment.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-features-grid"> {/* Grid structure maintained */}
              {/* --- NEW FEATURE CARDS (Mapped from Institutional & Patient Benefits) --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faShieldAlt} /></div>
                    <h4>Holistic Risk Intelligence</h4>
                    <p>Identifies financial leaks, compliance threats, governance gaps, and operational inefficiencies.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faHandHoldingDollar} /></div>
                    <h4>Strategic Transaction Enablement</h4>
                    <p>Fuels negotiations with accurate data, allowing confident assessment of deal value.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBuildingShield} /></div>
                    <h4>Regulatory & Ethical Shield</h4>
                    <p>Helps proactively mitigate reputational and legal risks associated with non-compliance.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faClipboardCheck} /></div> {/* Using generic check for planning */}
                    <h4>Seamless Integration Planning</h4>
                    <p>Enables smoother transitions in M&A through early harmonization of policies & workflows.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faHeartPulse} /></div>
                    <h4>Continuity & Quality of Care</h4>
                    <p>Ensures organizational changes don't disrupt care quality, timelines, or service access.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUserMd} /></div> {/* Representing improved outcomes */}
                    <h4>Improved Clinical Outcomes</h4>
                    <p>Reinforces performance standards impacting diagnostic accuracy and patient recovery.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- STRATEGIC IMPERATIVE (Detailed Planning Section) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Why Hospital Due Diligence is a Strategic Imperative</h2>
               <p>The first step toward transformation, credibility, and sustainable success.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-planning-items-wrapper"> {/* Kept existing wrapper class */}
                 {/* --- NEW PLANNING ITEMS (Mapped from 6 points) --- */}
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faShieldAlt} /></div>
                    <div className="cliniqor-content"><h4>Risk Mitigation</h4> <p>Detects legal disputes, licensing lapses, financial irregularities, and HR vulnerabilities.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faHandHoldingDollar} /></div>
                    <div className="cliniqor-content"><h4>Accurate Valuation</h4> <p>Delivers real-time valuation models based on EBITDA, assets, market position, and potential.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBinoculars} /></div>
                    <div className="cliniqor-content"><h4>Forward-Looking Insights</h4> <p>Unveils emerging risks, expansion opportunities, service optimization areas, and tech enablers.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBuildingShield} /></div>
                    <div className="cliniqor-content"><h4>Reputational Integrity</h4> <p>Preserves brand by uncovering dissatisfaction drivers, noncompliance, or clinical failures.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faClipboardCheck} /></div>
                    <div className="cliniqor-content"><h4>Compliance Confidence</h4> <p>Demonstrates regulatory preparedness and transparency to internal and external bodies.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faRoad} /></div>
                    <div className="cliniqor-content"><h4>Operational Roadmap</h4> <p>Lays the foundation for restructuring, digital transformation, automation, and quality improvement.</p> </div>
                 </div>
                 {/* --- END NEW PLANNING ITEMS --- */}
            </div>
          </div>
        </section>

        {/* --- HOW IT'S CONDUCTED / 6 TYPES (New Section using Features Layout) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light"> {/* Reusing features layout */}
           <div className="cliniqor-container">
              <div ref={title5Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title5Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>How is Hospital Due Diligence Conducted?</h2>
                 <p>A structured framework integrating assessments, reviews, and interviews across key domains.</p>
              </div>
              <div className="cliniqor-features-grid"> {/* Using features grid for the 6 types */}
                    {/* --- Cards for the 6 Types of Due Diligence --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCogs} /></div>
                            <h4>1. Operational Due Diligence</h4>
                            <p>Assesses core processes, capacity, utilization, departmental efficiency, and HIS/digital ecosystem.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCalculator} /></div>
                            <h4>2. Financial Due Diligence</h4>
                            <p>Examines financial viability, revenue channels, cost structures, debt, receivables, and projections.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsers} /></div>
                            <h4>3. Human Resource Due Diligence</h4>
                            <p>Evaluates staffing ratios, compliance (PF/ESIC), attrition, training, satisfaction, and HR policies.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faGavel} /></div>
                            <h4>4. Legal & Regulatory Due Diligence</h4>
                            <p>Validates licenses (Clinical Estab, Fire, AERB, etc.), land/building compliance, litigation, and agreements.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBuilding} /></div>
                            <h4>5. Infrastructure Due Diligence</h4>
                            <p>Scrutinizes structure, safety (fire/disaster), MGPS, HVAC, equipment lifecycle, and digital infrastructure.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUserMd} /></div>
                            <h4>6. Clinical Governance & Quality Assurance</h4>
                            <p>Assesses SOP adherence, quality indicators (HAI, mortality), audits, certifications, and patient feedback.</p>
                        </div>
                    </div>
                    {/* --- End Cards --- */}
               </div>
           </div>
        </section>

        {/* --- METHODOLOGY SECTION REMOVED --- */}
        {/*
        <section className="cliniqor-section cliniqor-section-bg-light">
             ... content removed ...
        </section>
        */}

        {/* --- RESULTS SECTION REMOVED --- */}
        {/*
        <section className="cliniqor-section">
             ... content removed ...
        </section>
        */}

        {/* --- WHY CHOOSE US SECTION REMOVED --- */}
        {/*
        <section className="cliniqor-section cliniqor-section-bg-dark cliniqor-why-choose-us-section">
             ... content removed ...
        </section>
        */}

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Expert Hospital Due Diligence Services</h3>
          <p>For institution-specific, end-to-end hospital due diligence tailored to your healthcare goals, partner with Cliniqor Healthcare Consulting. Elevating Healthcare through Strategic Intelligence.</p>
          {/* --- END UPDATED FOOTER CONTENT --- */}
          <a href="#contact" className="cliniqor-cta-button">Contact Us for Consultation</a> {/* Button kept */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;